import { valibotResolver } from "@hookform/resolvers/valibot";
import { CheckCircle, CopySimple, PlusCircle } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import { typedObjectEntries } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { ApiKeyPermission } from "common/types";

import { API_KEYS_PERMISSIONS } from "../../../constants";
import useApiTokens from "../../../datahooks/useApiTokens";
import useApiTokensMutations from "../../../datahooks/useApiTokensMutations";
import { ApiKeyNameSchema } from "../../../schema";
import PermissionOption from "./PermissionOption";

export default function CreateApiTokenButton({ size }: { size: "sm" | "md" }) {
  const { tokens } = useApiTokens();
  const { createApiToken, isCreatingToken } = useApiTokensMutations();
  const isDesktop = useTwBreakpoint("lg");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const {
    register,
    reset: resetForm,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    name: string;
  }>({
    resolver: valibotResolver(ApiKeyNameSchema),
    defaultValues: tokens && {
      name: `API Key #${tokens.length + 1}`,
    },
  });

  const [selectedPermission, setSelectedPermission] =
    useState<ApiKeyPermission>("all");

  const [isCopied, setIsCopied] = useState(false);
  const [apiToken, setApiToken] = useState<string>(null);

  const permissionOptions = typedObjectEntries(API_KEYS_PERMISSIONS).map(
    ([role, { label, description }]) => ({
      label,
      value: role,
      description,
    }),
  );

  function resetStates(): void {
    resetForm({
      name: `API Key #${tokens.length + 1}`,
    });

    setSelectedPermission("all");
    setTimeout(() => setApiToken(null), 200);
  }

  useEffect(() => {
    if (tokens) {
      resetForm({ name: `API Key #${tokens.length + 1}` });
    }
  }, [tokens]);

  function copyApiKey(): void {
    if (apiToken) {
      navigator.clipboard
        .writeText(apiToken)
        .then(() => {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 1000);
        })
        .catch((err) => {
          toast.error("Failed to copy API key: ", err);
        });
    }
  }

  function onSubmit({ name }: { name: string }): void {
    const isWritable = selectedPermission === "all";

    createApiToken({
      name,
      write: isWritable,
    }).then((response) => {
      setApiToken(response.token);
    });
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Title = isDesktop ? DialogTitle : DrawerTitle;

  const content = apiToken ? (
    <div className="flex flex-col items-center justify-center gap-6">
      <Title>Save your API key</Title>

      <FormField
        label="Key"
        htmlFor="api-key"
        hint="Keep your API key safe and do not share it with third parties"
        className="w-full"
      >
        <div className="flex gap-1.5">
          <Input value={apiToken} className="flex-1" id="api-key" disabled />
          <Button
            variant="outline-black"
            leftIcon={isCopied ? <CheckCircle /> : <CopySimple />}
            onClick={copyApiKey}
          >
            {isCopied ? "Copied!" : "Copy"}
          </Button>
        </div>
      </FormField>

      <div className="h-px w-full bg-black-100" />

      <div className="flex w-full flex-col gap-y-0.5">
        <span className="text-button-md">Permissions</span>

        <p className="text-black-500">
          {selectedPermission === "all" ? "Read and write" : "Read"} API
          resources
        </p>
      </div>

      <Button
        className="ml-auto w-fit"
        type="submit"
        onClick={() => setIsDialogOpen(false)}
      >
        Finish
      </Button>
    </div>
  ) : (
    <div className="flex flex-col items-center justify-center gap-6">
      <Title>Create API key</Title>

      <form
        className="flex w-full flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormField label="Key name" htmlFor="name" error={errors.name?.message}>
          <Input
            {...register("name")}
            placeholder="Enter name"
            aria-label="Enter key name"
            id="name"
          />
        </FormField>

        <div className="h-px w-full bg-black-100" />

        <div className="flex flex-col gap-2">
          <span className="text-button-sm">Choose permissions:</span>

          {permissionOptions.map((option) => {
            const { label, value, description } = option;

            return (
              <PermissionOption
                key={value}
                option={value}
                selectedPermission={selectedPermission}
                onPermissionChange={setSelectedPermission}
                label={label}
                description={description}
              />
            );
          })}
        </div>

        <Button
          isLoading={isCreatingToken}
          className="ml-auto w-fit"
          type="submit"
        >
          Next
        </Button>
      </form>
    </div>
  );

  return (
    <Component
      open={isDialogOpen}
      onOpenChange={setIsDialogOpen}
      onAfterClose={resetStates}
    >
      <Trigger asChild>
        <Button leftIcon={<PlusCircle weight="fill" />} size={size}>
          Create API Key
        </Button>
      </Trigger>
      <Content className="lg:px-6">{content}</Content>
    </Component>
  );
}
